/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	type Signal,
	inject,
	input,
	output,
	signal,
} from "@angular/core";
import {
	type IsActiveMatchOptions,
	Router,
	RouterModule,
} from "@angular/router";
import { CbRoutesUtils } from "@app/@classes/routes/cb-routes-utils";
import { RealtimeRoutePresenceService } from "@app/@services/generic/realtime-route-presence.service";
import { AuthTenantService } from "@app/@services/shared/auth-tenant.service";
import { BugReportDialogComponent } from "@app/modules/common/dialogs/bug-report-dialog/bug-report-dialog.component";
import { TranslateModule } from "@ngx-translate/core";
import { Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { DialogService } from "../dialog/dialog.service";
import { PopoverModule } from "../floating/popover/popover.module";
import { IconComponent } from "../icon/icon.component";
import { UserAvatarComponent } from "../user-avatar/user-avatar.component";

export type CbTopNavbarItem = {
	label?: string;
	icon?: string;
	counter?: number;
	color?: string;
	showSignal?: Signal<boolean>;
	description?: string;
	show?: () => boolean;
} & (
	| {
			type: "route";
			routerLink: string;
	  }
	| {
			type: "callback";
			callback: () => void;
	  }
	| {
			type: "group";
			items: CbTopNavbarItem[];
	  }
);

@Component({
	selector: "cb-top-navbar",
	imports: [
		CommonModule,
		IconComponent,
		PopoverModule,
		TranslateModule,
		RouterModule,
		UserAvatarComponent,
	],
	templateUrl: "./top-navbar.component.html",
	styleUrl: "./top-navbar.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopNavbarComponent {
	public readonly homeRoute = CbRoutesUtils.paths.choose_office.path;

	private readonly router: Router = inject(Router);

	private readonly tenantService: AuthTenantService = inject(AuthTenantService);
	private readonly routePresenceService: RealtimeRoutePresenceService = inject(
		RealtimeRoutePresenceService,
	);
	private readonly dialogService: DialogService = inject(DialogService);

	public readonly changeTenantUrl = CbRoutesUtils.paths.tenant.path;
	public readonly createTenantUrl = CbRoutesUtils.paths.tenant.create.path;
	public readonly userSettingsUrl = CbRoutesUtils.paths.w.settings.user.path;

	public readonly logo = input<string>();
	public readonly navItems = input<CbTopNavbarItem[]>([]);
	public readonly footerItems = input<CbTopNavbarItem[]>();
	public readonly background = input<string | null>();

	public readonly restrictedUserOptions = input<boolean>(false);

	public readonly userId = input<string | null>();
	public readonly profileName = input<string | null>();
	public readonly profileEmail = input<string | null>();

	public readonly tenantName = input<string | null>();

	public readonly currentRouteColor = signal<string | null>(null);

	public readonly logout = output<void>();

	public usersOnSameRoute = signal<string[]>([]);

	// Mobile hamburger menu state
	public isOpen = signal<boolean>(false);

	private destroy$ = new Subject<void>();

	private readonly subsetMatchOptions: IsActiveMatchOptions = {
		paths: "subset",
		queryParams: "subset",
		fragment: "ignored",
		matrixParams: "ignored",
	};

	/**
	 * Whether the environment is production.
	 */
	public get isProduction(): boolean {
		return environment.production;
	}

	public changeTenant(): void {
		this.tenantService.changeTenant();
	}

	public contactSupport(): void {
		// Open the email client with the support email
		// With the subject "Support Request"
		// And the body "Please describe your issue here"
		const header = `[${environment.projectName}] Richiesta di supporto`;
		const body = "Descrivi qui il tuo problema";

		window.open(`mailto:info@colibryx.com?subject=${header}&body=${body}`);
	}

	public reportBug(): void {
		this.dialogService.open(BugReportDialogComponent);
	}

	public routeActivationChanged(item: CbTopNavbarItem, isActive: boolean) {
		if (isActive) {
			this.currentRouteColor.set(item.color ?? null);
		}
	}

	public checkIsActive(item: CbTopNavbarItem): boolean {
		if (item.type === "route") {
			return this.router.isActive(item.routerLink, this.subsetMatchOptions);
		}
		if (item.type === "group") {
			return item.items.some((item) => this.checkIsActive(item));
		}
		return false;
	}

	public toggleOpen(): void {
		this.isOpen.set(!this.isOpen());
	}

	public closeMenu(): void {
		this.isOpen.set(false);
	}

	public handleItemClick(item: CbTopNavbarItem): void {
		if (item.type === "callback") {
			item.callback();
		}
		this.closeMenu();
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
